import { ClassifierService } from './../../services/classifier.service';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { DocumentTemplate, DocumentTemplateType } from '../../models/Template';

import { AppService } from '../../services/app.service';
import { TemplateService } from '../../services/template.service';
import { NgForm } from '@angular/forms';
import { createFileStub } from '../../shared/file/file.component';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { delay, finalize } from 'rxjs/operators';

interface IUserRight {
    Code: string;
    Text: string;
}

const cache: {
    userRights?: {
        Code: string,
        Text: string
    }[]
} = {};

@Component({
  selector: 'app-template-edit',
  templateUrl: './template-edit.component.html',
  styleUrls: ['./templates.component.scss']
})
export class TemplateEditComponent implements OnInit {
    constructor(
        public app: AppService,
        private service: TemplateService,
        private classifiers: ClassifierService,
        private activeModal: NgbActiveModal
    ) { }

    @Input() set data(value: DocumentTemplate) {
        Object.assign(this.item, value);

        if (value.Id && value.FileName) {
            this.fileLv = createFileStub(value.FileName);
            this.fileEn = createFileStub(value.FileName);
        }
    }

    readonly item = new DocumentTemplate();

    types: DocumentTemplateType[] = [];
    userRights: IUserRight[] = [];
    fileLv: File;
    fileEn: File;
    isSaving: boolean;

    readonly fileFormats = ['.docx', '.xlsx', '.jpg', '.png'];
    readonly fileFormatsDisplay = this.fileFormats.map(t => t.substring(1).toUpperCase()).join(', ');

    readonly typeDisplayFn = (type: DocumentTemplateType) => {
        return type ? this.app.translate(`documentTemplateType_${type}`) : '';
    }

    private _item: DocumentTemplate = new DocumentTemplate();

    @ViewChild('form', { static: false }) private form: NgForm;

    ngOnInit() {
        const user = this.app.currentUser;

        if (user) {
            if (user.hasRight('ES_MAIN.TEMPLATES.APPLICATION'))
                this.types.push(DocumentTemplateType.Application);

            if (user.hasRight('ES_MAIN.TEMPLATES.PURCHASE'))
                this.types.push(DocumentTemplateType.Purchase);

            if (user.hasRight('ES_MAIN.TEMPLATES.CERT_REQUEST') || user.hasRight('ES_MAIN.TEMPLATES.CERT_REQUEST_BY_UDR'))
                this.types.push(DocumentTemplateType.CertificateRequest);

            if (user.hasRight('ES_MAIN.TEMPLATES.FINAL_PAPER_AFFIRMATION'))
                this.types.push(DocumentTemplateType.FinalPaperAffirmation);

            if (user.hasRight('ES_MAIN.TEMPLATES.ON_CALL_SHIFT_REPORT'))
                this.types.push(DocumentTemplateType.OnCallShiftReport);

            if (user.hasRight('ES_MAIN.TEMPLATES.MANIPULATION_REPORT'))
                this.types.push(DocumentTemplateType.ManipulationReport);

            if (user.hasRight('ES_MAIN.TEMPLATES.ADD_GRADES_REPORT'))
                this.types.push(DocumentTemplateType.AddGradesReport);
        } else {
            this.types = [];
        }

        if (cache.userRights) {
            this.userRights = cache.userRights;
        } else {
            this.app.addLoading(this.classifiers.getUdr()).subscribe(data => {
                this.userRights = cache.userRights = data.filter(r => r.Code.startsWith("UDR")).map(t => ({ Code: t.Code, Text: t.Value }));
            });
        }
    }

    onFileChange(file: File, language: 'lv' | 'en') {
        let filename = file?.name;

        if (language == 'en') {
            if (!file) {
                this.item.FileEn = undefined;
            } else {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = (e) => {
                    const res = <string>reader.result;
                    this.item.FileEn = <any>res.substring(res.lastIndexOf(',') + 1);
                };
            }
        } else {
            if (!file) {
                this.item.FileLv = undefined;
            } else {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = (e) => {
                    const res = <string>reader.result;
                    this.item.FileLv = <any>res.substring(res.lastIndexOf(',') + 1);
                };
            }
        }

        if (!this.item.FileName && filename) {
            this.item.FileName = filename;
        } else if (!this.fileLv?.name && !this.fileEn?.name) {
            this.item.FileName = undefined;
        }
    }

    getFileUrl(item: DocumentTemplate, language: string) {
        return this.service.getFileUrl(item.Id, language);
    }

    save() {
        if (!this.form.valid) {
            this.app.alert.warning(this.app.translate('invalidFormWarning'));
            return;
        }

        this.isSaving = true;

        const success = () => {
            this.activeModal.close();
        };

        const req = this.item.Id
            ? this.service.update(this.item)
            : this.service.add(this.item);

        req.pipe(delay(500), finalize(() => this.isSaving = false)).subscribe(success);
    }

    cancel() {
        this.activeModal.dismiss();
    }
}
